import { b2Mat22, b2Math, b2Vec2 } from "./common/math.js";
import { b2Settings } from "./common/settings.js";

export class b2Jacobian {
  constructor() {
    this.linearA = new b2Vec2();
    this.linearB = new b2Vec2();
    this.angularA = 0;
    this.angularB = 0;
  }

  SetZero() {
    this.linearA.SetZero();
    this.linearB.SetZero();
    this.angularA = 0;
    this.angularB = 0;
  }

  Compute(linearA, angularA, linearB, angularB) {
    return (
      this.linearA.x * linearA.x + this.linearA.y * linearA.y + this.angularA * angularA
      + this.linearB.x * linearB.x + this.linearB.y * linearB.y + this.angularB * angularB
    );
  }
}

export class b2JointEdge {
  constructor() {
    this.other = null;
    this.joint = null;
    this.prev = null;
    this.next = null;
  }
}

export class b2JointDef {
  constructor() {
    this.type = b2Joint.e_unknownJoint;
    this.userData = null;
    this.bodyA = null;
    this.bodyB = null;
    this.body1 = null;
    this.body2 = null;
    this.collideConnected = false;
  }
}

export class b2DistanceJointDef extends b2JointDef {
  constructor() {
    super();
    this.type = b2Joint.e_distanceJoint;
    this.localAnchorA = new b2Vec2();
    this.localAnchorB = new b2Vec2();
    this.localAnchor1 = this.localAnchorA;
    this.localAnchor2 = this.localAnchorB;
    this.length = 1;
    this.frequencyHz = 0;
    this.dampingRatio = 0;
  }

  Initialize(bodyA, bodyB, anchorA, anchorB) {
    this.bodyA = this.body1 = bodyA;
    this.bodyB = this.body2 = bodyB;
    this.localAnchorA.SetV(bodyA.GetLocalPoint(anchorA));
    this.localAnchorB.SetV(bodyB.GetLocalPoint(anchorB));
    this.localAnchor1 = this.localAnchorA;
    this.localAnchor2 = this.localAnchorB;
    const dx = anchorB.x - anchorA.x;
    const dy = anchorB.y - anchorA.y;
    this.length = Math.sqrt(dx * dx + dy * dy);
    this.frequencyHz = 0;
    this.dampingRatio = 0;
  }
}

export class b2PrismaticJointDef extends b2JointDef {
  constructor() {
    super();
    this.type = b2Joint.e_prismaticJoint;
    this.localAnchorA = new b2Vec2();
    this.localAnchorB = new b2Vec2();
    this.localAnchor1 = this.localAnchorA;
    this.localAnchor2 = this.localAnchorB;
    this.localAxisA = new b2Vec2(1, 0);
    this.localAxis1 = this.localAxisA;
    this.referenceAngle = 0;
    this.enableLimit = false;
    this.lowerTranslation = 0;
    this.upperTranslation = 0;
    this.enableMotor = false;
    this.maxMotorForce = 0;
    this.motorSpeed = 0;
  }

  Initialize(bodyA, bodyB, anchor, axis) {
    this.bodyA = this.body1 = bodyA;
    this.bodyB = this.body2 = bodyB;
    this.localAnchorA = bodyA.GetLocalPoint(anchor);
    this.localAnchorB = bodyB.GetLocalPoint(anchor);
    this.localAnchor1 = this.localAnchorA;
    this.localAnchor2 = this.localAnchorB;
    this.localAxisA = bodyA.GetLocalVector(axis);
    this.localAxis1 = this.localAxisA;
    this.referenceAngle = bodyB.GetAngle() - bodyA.GetAngle();
  }
}

export class b2RevoluteJointDef extends b2JointDef {
  constructor() {
    super();
    this.type = b2Joint.e_revoluteJoint;
    this.localAnchorA = new b2Vec2();
    this.localAnchorB = new b2Vec2();
    this.localAnchor1 = this.localAnchorA;
    this.localAnchor2 = this.localAnchorB;
    this.referenceAngle = 0;
    this.lowerAngle = 0;
    this.upperAngle = 0;
    this.maxMotorTorque = 0;
    this.motorSpeed = 0;
    this.enableLimit = false;
    this.enableMotor = false;
  }

  Initialize(bodyA, bodyB, anchor) {
    this.bodyA = this.body1 = bodyA;
    this.bodyB = this.body2 = bodyB;
    this.localAnchorA = bodyA.GetLocalPoint(anchor);
    this.localAnchorB = bodyB.GetLocalPoint(anchor);
    this.localAnchor1 = this.localAnchorA;
    this.localAnchor2 = this.localAnchorB;
    this.referenceAngle = bodyB.GetAngle() - bodyA.GetAngle();
  }
}

export class b2Joint {
  static e_unknownJoint = 0;
  static e_revoluteJoint = 1;
  static e_prismaticJoint = 2;
  static e_distanceJoint = 3;
  static e_pulleyJoint = 4;
  static e_mouseJoint = 5;
  static e_gearJoint = 6;
  static e_inactiveLimit = 0;
  static e_atLowerLimit = 1;
  static e_atUpperLimit = 2;
  static e_equalLimits = 3;

  constructor(def) {
    this.m_type = def.type;
    this.m_prev = null;
    this.m_next = null;
    this.m_bodyA = def.bodyA ?? def.body1;
    this.m_bodyB = def.bodyB ?? def.body2;
    this.m_body1 = this.m_bodyA;
    this.m_body2 = this.m_bodyB;
    this.m_collideConnected = def.collideConnected;
    this.m_islandFlag = false;
    this.m_userData = def.userData;
    this.m_nodeA = new b2JointEdge();
    this.m_nodeB = new b2JointEdge();
    this.m_node1 = this.m_nodeA;
    this.m_node2 = this.m_nodeB;
  }

  static Create(def) {
    switch (def.type) {
      case b2Joint.e_distanceJoint:
        return new b2DistanceJoint(def);
      case b2Joint.e_prismaticJoint:
        return new b2PrismaticJoint(def);
      case b2Joint.e_revoluteJoint:
        return new b2RevoluteJoint(def);
      default:
        return null;
    }
  }

  static Destroy() {}

  GetAnchorA() {
    return null;
  }

  GetAnchorB() {
    return null;
  }

  GetBodyA() {
    return this.m_bodyA;
  }

  GetBodyB() {
    return this.m_bodyB;
  }

  GetReactionForce() {
    return new b2Vec2();
  }

  GetReactionTorque() {
    return 0;
  }

  InitVelocityConstraints() {}

  SolveVelocityConstraints() {}

  FinalizeVelocityConstraints() {}

  InitPositionConstraints() {}

  SolvePositionConstraints() {
    return true;
  }
}

export class b2DistanceJoint extends b2Joint {
  constructor(def) {
    super(def);
    // AS3 b2DistanceJoint copies anchors out of the shared JointDef.
    // Level scripts reuse the same JointDef instance across many joints, so
    // retaining references here corrupts previously created joints.
    this.m_localAnchor1 = (def.localAnchorA ?? def.localAnchor1).Copy();
    this.m_localAnchor2 = (def.localAnchorB ?? def.localAnchor2).Copy();
    this.m_length = def.length;
    this.m_frequencyHz = def.frequencyHz;
    this.m_dampingRatio = def.dampingRatio;
    this.m_impulse = 0;
    this.m_gamma = 0;
    this.m_bias = 0;
    this.m_inv_dt = 0;
    this.m_u = new b2Vec2();
    this.m_mass = 0;
  }

  GetAnchorA() {
    return this.m_bodyA.GetWorldPoint(this.m_localAnchor1);
  }

  GetAnchorB() {
    return this.m_bodyB.GetWorldPoint(this.m_localAnchor2);
  }

  GetReactionForce(invDt = 0) {
    const force = this.m_u.Copy();
    force.Multiply((invDt || this.m_inv_dt) * this.m_impulse);
    return force;
  }

  GetReactionTorque() {
    return 0;
  }

  InitVelocityConstraints(step) {
    this.m_inv_dt = step.inv_dt;
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    this.m_u.x = bodyB.m_sweep.c.x + otherLocalX - bodyA.m_sweep.c.x - localX;
    this.m_u.y = bodyB.m_sweep.c.y + otherLocalY - bodyA.m_sweep.c.y - localY;
    const length = Math.sqrt(this.m_u.x * this.m_u.x + this.m_u.y * this.m_u.y);
    if (length > b2Settings.b2_linearSlop) {
      this.m_u.Multiply(1 / length);
    } else {
      this.m_u.SetZero();
    }
    const crossA = localX * this.m_u.y - localY * this.m_u.x;
    const crossB = otherLocalX * this.m_u.y - otherLocalY * this.m_u.x;
    const inverseMass = bodyA.m_invMass + bodyA.m_invI * crossA * crossA + bodyB.m_invMass + bodyB.m_invI * crossB * crossB;
    this.m_mass = 1 / inverseMass;
    if (this.m_frequencyHz > 0) {
      const error = length - this.m_length;
      const omega = 2 * Math.PI * this.m_frequencyHz;
      const damping = 2 * this.m_mass * this.m_dampingRatio * omega;
      const stiffness = this.m_mass * omega * omega;
      this.m_gamma = 1 / (step.dt * (damping + step.dt * stiffness));
      this.m_bias = error * step.dt * stiffness * this.m_gamma;
      this.m_mass = 1 / (inverseMass + this.m_gamma);
    }
    if (step.warmStarting) {
      this.m_impulse *= step.dtRatio;
      const impulseX = this.m_impulse * this.m_u.x;
      const impulseY = this.m_impulse * this.m_u.y;
      bodyA.m_linearVelocity.x -= bodyA.m_invMass * impulseX;
      bodyA.m_linearVelocity.y -= bodyA.m_invMass * impulseY;
      bodyA.m_angularVelocity -= bodyA.m_invI * (localX * impulseY - localY * impulseX);
      bodyB.m_linearVelocity.x += bodyB.m_invMass * impulseX;
      bodyB.m_linearVelocity.y += bodyB.m_invMass * impulseY;
      bodyB.m_angularVelocity += bodyB.m_invI * (otherLocalX * impulseY - otherLocalY * impulseX);
    } else {
      this.m_impulse = 0;
    }
  }

  SolveVelocityConstraints() {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    const velocityAX = bodyA.m_linearVelocity.x - bodyA.m_angularVelocity * localY;
    const velocityAY = bodyA.m_linearVelocity.y + bodyA.m_angularVelocity * localX;
    const velocityBX = bodyB.m_linearVelocity.x - bodyB.m_angularVelocity * otherLocalY;
    const velocityBY = bodyB.m_linearVelocity.y + bodyB.m_angularVelocity * otherLocalX;
    const constraintVelocity = this.m_u.x * (velocityBX - velocityAX) + this.m_u.y * (velocityBY - velocityAY);
    const impulse = -this.m_mass * (constraintVelocity + this.m_bias + this.m_gamma * this.m_impulse);
    this.m_impulse += impulse;
    const impulseX = impulse * this.m_u.x;
    const impulseY = impulse * this.m_u.y;
    bodyA.m_linearVelocity.x -= bodyA.m_invMass * impulseX;
    bodyA.m_linearVelocity.y -= bodyA.m_invMass * impulseY;
    bodyA.m_angularVelocity -= bodyA.m_invI * (localX * impulseY - localY * impulseX);
    bodyB.m_linearVelocity.x += bodyB.m_invMass * impulseX;
    bodyB.m_linearVelocity.y += bodyB.m_invMass * impulseY;
    bodyB.m_angularVelocity += bodyB.m_invI * (otherLocalX * impulseY - otherLocalY * impulseX);
  }

  SolvePositionConstraints() {
    if (this.m_frequencyHz > 0) {
      return true;
    }
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    let axisX = bodyB.m_sweep.c.x + otherLocalX - bodyA.m_sweep.c.x - localX;
    let axisY = bodyB.m_sweep.c.y + otherLocalY - bodyA.m_sweep.c.y - localY;
    const length = Math.sqrt(axisX * axisX + axisY * axisY);
    axisX /= length;
    axisY /= length;
    let constraint = length - this.m_length;
    constraint = b2Math.Clamp(constraint, -b2Settings.b2_maxLinearCorrection, b2Settings.b2_maxLinearCorrection);
    const impulse = -this.m_mass * constraint;
    this.m_u.Set(axisX, axisY);
    const impulseX = impulse * this.m_u.x;
    const impulseY = impulse * this.m_u.y;
    bodyA.m_sweep.c.x -= bodyA.m_invMass * impulseX;
    bodyA.m_sweep.c.y -= bodyA.m_invMass * impulseY;
    bodyA.m_sweep.a -= bodyA.m_invI * (localX * impulseY - localY * impulseX);
    bodyB.m_sweep.c.x += bodyB.m_invMass * impulseX;
    bodyB.m_sweep.c.y += bodyB.m_invMass * impulseY;
    bodyB.m_sweep.a += bodyB.m_invI * (otherLocalX * impulseY - otherLocalY * impulseX);
    bodyA.SynchronizeTransform();
    bodyB.SynchronizeTransform();
    return b2Math.Abs(constraint) < b2Settings.b2_linearSlop;
  }
}

export class b2PrismaticJoint extends b2Joint {
  constructor(def) {
    super(def);
    this.m_localAnchor1 = (def.localAnchorA ?? def.localAnchor1).Copy();
    this.m_localAnchor2 = (def.localAnchorB ?? def.localAnchor2).Copy();
    this.m_localXAxis1 = (def.localAxisA ?? def.localAxis1).Copy();
    this.m_localYAxis1 = new b2Vec2(-this.m_localXAxis1.y, this.m_localXAxis1.x);
    this.m_refAngle = def.referenceAngle;
    this.m_linearJacobian = new b2Jacobian();
    this.m_linearMass = 0;
    this.m_force = 0;
    this.m_angularMass = 0;
    this.m_torque = 0;
    this.m_motorJacobian = new b2Jacobian();
    this.m_motorMass = 0;
    this.m_motorForce = 0;
    this.m_limitForce = 0;
    this.m_limitPositionImpulse = 0;
    this.m_lowerTranslation = def.lowerTranslation;
    this.m_upperTranslation = def.upperTranslation;
    this.m_maxMotorForce = def.maxMotorForce;
    this.m_motorSpeed = def.motorSpeed;
    this.m_enableLimit = def.enableLimit;
    this.m_enableMotor = def.enableMotor;
    this.m_limitState = b2Joint.e_inactiveLimit;
  }

  GetAnchorA() {
    return this.m_bodyA.GetWorldPoint(this.m_localAnchor1);
  }

  GetAnchorB() {
    return this.m_bodyB.GetWorldPoint(this.m_localAnchor2);
  }

  GetReactionForce() {
    const rotation = this.m_bodyA.m_xf.R;
    const axisX = rotation.col1.x * this.m_localXAxis1.x + rotation.col2.x * this.m_localXAxis1.y;
    const axisY = rotation.col1.y * this.m_localXAxis1.x + rotation.col2.y * this.m_localXAxis1.y;
    const perpX = rotation.col1.x * this.m_localYAxis1.x + rotation.col2.x * this.m_localYAxis1.y;
    const perpY = rotation.col1.y * this.m_localYAxis1.x + rotation.col2.y * this.m_localYAxis1.y;
    return new b2Vec2(this.m_limitForce * axisX + this.m_force * perpX, this.m_limitForce * axisY + this.m_force * perpY);
  }

  GetReactionTorque() {
    return this.m_torque;
  }

  GetJointTranslation() {
    const anchorA = this.m_bodyA.GetWorldPoint(this.m_localAnchor1);
    const anchorB = this.m_bodyB.GetWorldPoint(this.m_localAnchor2);
    const dx = anchorB.x - anchorA.x;
    const dy = anchorB.y - anchorA.y;
    const axis = this.m_bodyA.GetWorldVector(this.m_localXAxis1);
    return axis.x * dx + axis.y * dy;
  }

  GetJointSpeed() {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    const pointAX = bodyA.m_sweep.c.x + localX;
    const pointAY = bodyA.m_sweep.c.y + localY;
    const pointBX = bodyB.m_sweep.c.x + otherLocalX;
    const pointBY = bodyB.m_sweep.c.y + otherLocalY;
    const dx = pointBX - pointAX;
    const dy = pointBY - pointAY;
    const axis = bodyA.GetWorldVector(this.m_localXAxis1);
    const velocityA = bodyA.m_linearVelocity;
    const velocityB = bodyB.m_linearVelocity;
    const angularVelocityA = bodyA.m_angularVelocity;
    const angularVelocityB = bodyB.m_angularVelocity;
    return dx * (-angularVelocityA * axis.y)
      + dy * (angularVelocityA * axis.x)
      + (
        axis.x * (velocityB.x - angularVelocityB * otherLocalY - velocityA.x + angularVelocityA * localY)
        + axis.y * (velocityB.y + angularVelocityB * otherLocalX - velocityA.y - angularVelocityA * localX)
      );
  }

  IsLimitEnabled() {
    return this.m_enableLimit;
  }

  EnableLimit(flag) {
    this.m_enableLimit = flag;
  }

  GetLowerLimit() {
    return this.m_lowerTranslation;
  }

  GetUpperLimit() {
    return this.m_upperTranslation;
  }

  SetLimits(lower, upper) {
    this.m_lowerTranslation = lower;
    this.m_upperTranslation = upper;
  }

  IsMotorEnabled() {
    return this.m_enableMotor;
  }

  EnableMotor(flag) {
    this.m_enableMotor = flag;
  }

  SetMotorSpeed(speed) {
    this.m_motorSpeed = speed;
  }

  GetMotorSpeed() {
    return this.m_motorSpeed;
  }

  SetMaxMotorForce(force) {
    this.m_maxMotorForce = force;
  }

  GetMotorForce() {
    return this.m_motorForce;
  }

  InitVelocityConstraints(step) {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    const invMassA = bodyA.m_invMass;
    const invMassB = bodyB.m_invMass;
    const invIA = bodyA.m_invI;
    const invIB = bodyB.m_invI;
    rotation = bodyA.m_xf.R;
    const perpX = rotation.col1.x * this.m_localYAxis1.x + rotation.col2.x * this.m_localYAxis1.y;
    const perpY = rotation.col1.y * this.m_localYAxis1.x + rotation.col2.y * this.m_localYAxis1.y;
    const deltaX = bodyB.m_sweep.c.x + otherLocalX - bodyA.m_sweep.c.x;
    const deltaY = bodyB.m_sweep.c.y + otherLocalY - bodyA.m_sweep.c.y;
    this.m_linearJacobian.linearA.x = -perpX;
    this.m_linearJacobian.linearA.y = -perpY;
    this.m_linearJacobian.linearB.x = perpX;
    this.m_linearJacobian.linearB.y = perpY;
    this.m_linearJacobian.angularA = -(deltaX * perpY - deltaY * perpX);
    this.m_linearJacobian.angularB = otherLocalX * perpY - otherLocalY * perpX;
    this.m_linearMass = invMassA
      + invIA * this.m_linearJacobian.angularA * this.m_linearJacobian.angularA
      + invMassB
      + invIB * this.m_linearJacobian.angularB * this.m_linearJacobian.angularB;
    this.m_linearMass = 1 / this.m_linearMass;
    this.m_angularMass = invIA + invIB;
    if (this.m_angularMass > Number.MIN_VALUE) {
      this.m_angularMass = 1 / this.m_angularMass;
    }
    if (this.m_enableLimit || this.m_enableMotor) {
      rotation = bodyA.m_xf.R;
      const axisX = rotation.col1.x * this.m_localXAxis1.x + rotation.col2.x * this.m_localXAxis1.y;
      const axisY = rotation.col1.y * this.m_localXAxis1.x + rotation.col2.y * this.m_localXAxis1.y;
      this.m_motorJacobian.linearA.x = -axisX;
      this.m_motorJacobian.linearA.y = -axisY;
      this.m_motorJacobian.linearB.x = axisX;
      this.m_motorJacobian.linearB.y = axisY;
      this.m_motorJacobian.angularA = -(deltaX * axisY - deltaY * axisX);
      this.m_motorJacobian.angularB = otherLocalX * axisY - otherLocalY * axisX;
      this.m_motorMass = invMassA
        + invIA * this.m_motorJacobian.angularA * this.m_motorJacobian.angularA
        + invMassB
        + invIB * this.m_motorJacobian.angularB * this.m_motorJacobian.angularB;
      this.m_motorMass = 1 / this.m_motorMass;
      if (this.m_enableLimit) {
        const translationX = deltaX - localX;
        const translationY = deltaY - localY;
        const translation = axisX * translationX + axisY * translationY;
        if (b2Math.Abs(this.m_upperTranslation - this.m_lowerTranslation) < 2 * b2Settings.b2_linearSlop) {
          this.m_limitState = b2Joint.e_equalLimits;
        } else if (translation <= this.m_lowerTranslation) {
          if (this.m_limitState !== b2Joint.e_atLowerLimit) {
            this.m_limitForce = 0;
          }
          this.m_limitState = b2Joint.e_atLowerLimit;
        } else if (translation >= this.m_upperTranslation) {
          if (this.m_limitState !== b2Joint.e_atUpperLimit) {
            this.m_limitForce = 0;
          }
          this.m_limitState = b2Joint.e_atUpperLimit;
        } else {
          this.m_limitState = b2Joint.e_inactiveLimit;
          this.m_limitForce = 0;
        }
      }
    }
    if (this.m_enableMotor === false) {
      this.m_motorForce = 0;
    }
    if (this.m_enableLimit === false) {
      this.m_limitForce = 0;
    }
    if (step.warmStarting) {
      const velocityAX = step.dt * (
        this.m_force * this.m_linearJacobian.linearA.x
        + (this.m_motorForce + this.m_limitForce) * this.m_motorJacobian.linearA.x
      );
      const velocityAY = step.dt * (
        this.m_force * this.m_linearJacobian.linearA.y
        + (this.m_motorForce + this.m_limitForce) * this.m_motorJacobian.linearA.y
      );
      const velocityBX = step.dt * (
        this.m_force * this.m_linearJacobian.linearB.x
        + (this.m_motorForce + this.m_limitForce) * this.m_motorJacobian.linearB.x
      );
      const velocityBY = step.dt * (
        this.m_force * this.m_linearJacobian.linearB.y
        + (this.m_motorForce + this.m_limitForce) * this.m_motorJacobian.linearB.y
      );
      const angularVelocityA = step.dt * (
        this.m_force * this.m_linearJacobian.angularA
        - this.m_torque
        + (this.m_motorForce + this.m_limitForce) * this.m_motorJacobian.angularA
      );
      const angularVelocityB = step.dt * (
        this.m_force * this.m_linearJacobian.angularB
        + this.m_torque
        + (this.m_motorForce + this.m_limitForce) * this.m_motorJacobian.angularB
      );
      bodyA.m_linearVelocity.x += invMassA * velocityAX;
      bodyA.m_linearVelocity.y += invMassA * velocityAY;
      bodyA.m_angularVelocity += invIA * angularVelocityA;
      bodyB.m_linearVelocity.x += invMassB * velocityBX;
      bodyB.m_linearVelocity.y += invMassB * velocityBY;
      bodyB.m_angularVelocity += invIB * angularVelocityB;
    } else {
      this.m_force = 0;
      this.m_torque = 0;
      this.m_limitForce = 0;
      this.m_motorForce = 0;
    }
    this.m_limitPositionImpulse = 0;
  }

  SolveVelocityConstraints(step) {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    const invMassA = bodyA.m_invMass;
    const invMassB = bodyB.m_invMass;
    const invIA = bodyA.m_invI;
    const invIB = bodyB.m_invI;
    let constraintVelocity = this.m_linearJacobian.Compute(
      bodyA.m_linearVelocity,
      bodyA.m_angularVelocity,
      bodyB.m_linearVelocity,
      bodyB.m_angularVelocity,
    );
    let forceDelta = -step.inv_dt * this.m_linearMass * constraintVelocity;
    this.m_force += forceDelta;
    let impulse = step.dt * forceDelta;
    bodyA.m_linearVelocity.x += invMassA * impulse * this.m_linearJacobian.linearA.x;
    bodyA.m_linearVelocity.y += invMassA * impulse * this.m_linearJacobian.linearA.y;
    bodyA.m_angularVelocity += invIA * impulse * this.m_linearJacobian.angularA;
    bodyB.m_linearVelocity.x += invMassB * impulse * this.m_linearJacobian.linearB.x;
    bodyB.m_linearVelocity.y += invMassB * impulse * this.m_linearJacobian.linearB.y;
    bodyB.m_angularVelocity += invIB * impulse * this.m_linearJacobian.angularB;
    constraintVelocity = bodyB.m_angularVelocity - bodyA.m_angularVelocity;
    let torqueDelta = -step.inv_dt * this.m_angularMass * constraintVelocity;
    this.m_torque += torqueDelta;
    impulse = step.dt * torqueDelta;
    bodyA.m_angularVelocity -= invIA * impulse;
    bodyB.m_angularVelocity += invIB * impulse;
    if (this.m_enableMotor && this.m_limitState !== b2Joint.e_equalLimits) {
      let motorVelocity = this.m_motorJacobian.Compute(
        bodyA.m_linearVelocity,
        bodyA.m_angularVelocity,
        bodyB.m_linearVelocity,
        bodyB.m_angularVelocity,
      ) - this.m_motorSpeed;
      let motorForceDelta = -step.inv_dt * this.m_motorMass * motorVelocity;
      const previousMotorForce = this.m_motorForce;
      this.m_motorForce = b2Math.Clamp(this.m_motorForce + motorForceDelta, -this.m_maxMotorForce, this.m_maxMotorForce);
      motorForceDelta = this.m_motorForce - previousMotorForce;
      impulse = step.dt * motorForceDelta;
      bodyA.m_linearVelocity.x += invMassA * impulse * this.m_motorJacobian.linearA.x;
      bodyA.m_linearVelocity.y += invMassA * impulse * this.m_motorJacobian.linearA.y;
      bodyA.m_angularVelocity += invIA * impulse * this.m_motorJacobian.angularA;
      bodyB.m_linearVelocity.x += invMassB * impulse * this.m_motorJacobian.linearB.x;
      bodyB.m_linearVelocity.y += invMassB * impulse * this.m_motorJacobian.linearB.y;
      bodyB.m_angularVelocity += invIB * impulse * this.m_motorJacobian.angularB;
    }
    if (this.m_enableLimit && this.m_limitState !== b2Joint.e_inactiveLimit) {
      let limitVelocity = this.m_motorJacobian.Compute(
        bodyA.m_linearVelocity,
        bodyA.m_angularVelocity,
        bodyB.m_linearVelocity,
        bodyB.m_angularVelocity,
      );
      let limitForceDelta = -step.inv_dt * this.m_motorMass * limitVelocity;
      if (this.m_limitState === b2Joint.e_equalLimits) {
        this.m_limitForce += limitForceDelta;
      } else if (this.m_limitState === b2Joint.e_atLowerLimit) {
        const previousLimitForce = this.m_limitForce;
        this.m_limitForce = b2Math.Max(this.m_limitForce + limitForceDelta, 0);
        limitForceDelta = this.m_limitForce - previousLimitForce;
      } else if (this.m_limitState === b2Joint.e_atUpperLimit) {
        const previousLimitForce = this.m_limitForce;
        this.m_limitForce = b2Math.Min(this.m_limitForce + limitForceDelta, 0);
        limitForceDelta = this.m_limitForce - previousLimitForce;
      }
      impulse = step.dt * limitForceDelta;
      bodyA.m_linearVelocity.x += invMassA * impulse * this.m_motorJacobian.linearA.x;
      bodyA.m_linearVelocity.y += invMassA * impulse * this.m_motorJacobian.linearA.y;
      bodyA.m_angularVelocity += invIA * impulse * this.m_motorJacobian.angularA;
      bodyB.m_linearVelocity.x += invMassB * impulse * this.m_motorJacobian.linearB.x;
      bodyB.m_linearVelocity.y += invMassB * impulse * this.m_motorJacobian.linearB.y;
      bodyB.m_angularVelocity += invIB * impulse * this.m_motorJacobian.angularB;
    }
  }

  SolvePositionConstraints() {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    const invMassA = bodyA.m_invMass;
    const invMassB = bodyB.m_invMass;
    const invIA = bodyA.m_invI;
    const invIB = bodyB.m_invI;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    let pointAX = bodyA.m_sweep.c.x + localX;
    let pointAY = bodyA.m_sweep.c.y + localY;
    let pointBX = bodyB.m_sweep.c.x + otherLocalX;
    let pointBY = bodyB.m_sweep.c.y + otherLocalY;
    let dx = pointBX - pointAX;
    let dy = pointBY - pointAY;
    rotation = bodyA.m_xf.R;
    const perpX = rotation.col1.x * this.m_localYAxis1.x + rotation.col2.x * this.m_localYAxis1.y;
    const perpY = rotation.col1.y * this.m_localYAxis1.x + rotation.col2.y * this.m_localYAxis1.y;
    let linearConstraint = perpX * dx + perpY * dy;
    linearConstraint = b2Math.Clamp(linearConstraint, -b2Settings.b2_maxLinearCorrection, b2Settings.b2_maxLinearCorrection);
    const linearImpulse = -this.m_linearMass * linearConstraint;
    bodyA.m_sweep.c.x += invMassA * linearImpulse * this.m_linearJacobian.linearA.x;
    bodyA.m_sweep.c.y += invMassA * linearImpulse * this.m_linearJacobian.linearA.y;
    bodyA.m_sweep.a += invIA * linearImpulse * this.m_linearJacobian.angularA;
    bodyB.m_sweep.c.x += invMassB * linearImpulse * this.m_linearJacobian.linearB.x;
    bodyB.m_sweep.c.y += invMassB * linearImpulse * this.m_linearJacobian.linearB.y;
    bodyB.m_sweep.a += invIB * linearImpulse * this.m_linearJacobian.angularB;
    let linearError = b2Math.Abs(linearConstraint);
    let angularConstraint = bodyB.m_sweep.a - bodyA.m_sweep.a - this.m_refAngle;
    angularConstraint = b2Math.Clamp(angularConstraint, -b2Settings.b2_maxAngularCorrection, b2Settings.b2_maxAngularCorrection);
    const angularImpulse = -this.m_angularMass * angularConstraint;
    bodyA.m_sweep.a -= bodyA.m_invI * angularImpulse;
    bodyB.m_sweep.a += bodyB.m_invI * angularImpulse;
    bodyA.SynchronizeTransform();
    bodyB.SynchronizeTransform();
    const angularError = b2Math.Abs(angularConstraint);
    if (this.m_enableLimit && this.m_limitState !== b2Joint.e_inactiveLimit) {
      rotation = bodyA.m_xf.R;
      localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
      localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
      rotated = rotation.col1.x * localX + rotation.col2.x * localY;
      localY = rotation.col1.y * localX + rotation.col2.y * localY;
      localX = rotated;
      rotation = bodyB.m_xf.R;
      otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
      otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
      rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
      otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
      otherLocalX = rotated;
      pointAX = bodyA.m_sweep.c.x + localX;
      pointAY = bodyA.m_sweep.c.y + localY;
      pointBX = bodyB.m_sweep.c.x + otherLocalX;
      pointBY = bodyB.m_sweep.c.y + otherLocalY;
      dx = pointBX - pointAX;
      dy = pointBY - pointAY;
      rotation = bodyA.m_xf.R;
      const axisX = rotation.col1.x * this.m_localXAxis1.x + rotation.col2.x * this.m_localXAxis1.y;
      const axisY = rotation.col1.y * this.m_localXAxis1.x + rotation.col2.y * this.m_localXAxis1.y;
      const translation = axisX * dx + axisY * dy;
      let limitImpulse = 0;
      if (this.m_limitState === b2Joint.e_equalLimits) {
        let limitConstraint = b2Math.Clamp(translation, -b2Settings.b2_maxLinearCorrection, b2Settings.b2_maxLinearCorrection);
        limitImpulse = -this.m_motorMass * limitConstraint;
        linearError = b2Math.Max(linearError, b2Math.Abs(angularConstraint));
      } else if (this.m_limitState === b2Joint.e_atLowerLimit) {
        let limitConstraint = translation - this.m_lowerTranslation;
        linearError = b2Math.Max(linearError, -limitConstraint);
        limitConstraint = b2Math.Clamp(limitConstraint + b2Settings.b2_linearSlop, -b2Settings.b2_maxLinearCorrection, 0);
        limitImpulse = -this.m_motorMass * limitConstraint;
        const previousPositionImpulse = this.m_limitPositionImpulse;
        this.m_limitPositionImpulse = b2Math.Max(this.m_limitPositionImpulse + limitImpulse, 0);
        limitImpulse = this.m_limitPositionImpulse - previousPositionImpulse;
      } else if (this.m_limitState === b2Joint.e_atUpperLimit) {
        let limitConstraint = translation - this.m_upperTranslation;
        linearError = b2Math.Max(linearError, limitConstraint);
        limitConstraint = b2Math.Clamp(limitConstraint - b2Settings.b2_linearSlop, 0, b2Settings.b2_maxLinearCorrection);
        limitImpulse = -this.m_motorMass * limitConstraint;
        const previousPositionImpulse = this.m_limitPositionImpulse;
        this.m_limitPositionImpulse = b2Math.Min(this.m_limitPositionImpulse + limitImpulse, 0);
        limitImpulse = this.m_limitPositionImpulse - previousPositionImpulse;
      }
      bodyA.m_sweep.c.x += invMassA * limitImpulse * this.m_motorJacobian.linearA.x;
      bodyA.m_sweep.c.y += invMassA * limitImpulse * this.m_motorJacobian.linearA.y;
      bodyA.m_sweep.a += invIA * limitImpulse * this.m_motorJacobian.angularA;
      bodyB.m_sweep.c.x += invMassB * limitImpulse * this.m_motorJacobian.linearB.x;
      bodyB.m_sweep.c.y += invMassB * limitImpulse * this.m_motorJacobian.linearB.y;
      bodyB.m_sweep.a += invIB * limitImpulse * this.m_motorJacobian.angularB;
      bodyA.SynchronizeTransform();
      bodyB.SynchronizeTransform();
    }
    return linearError <= b2Settings.b2_linearSlop && angularError <= b2Settings.b2_angularSlop;
  }
}

export class b2RevoluteJoint extends b2Joint {
  static tImpulse = new b2Vec2();

  constructor(def) {
    super(def);
    this.m_localAnchor1 = (def.localAnchorA ?? def.localAnchor1).Copy();
    this.m_localAnchor2 = (def.localAnchorB ?? def.localAnchor2).Copy();
    this.m_referenceAngle = def.referenceAngle;
    this.m_pivotForce = new b2Vec2();
    this.m_motorForce = 0;
    this.m_limitForce = 0;
    this.m_limitPositionImpulse = 0;
    this.m_lowerAngle = def.lowerAngle;
    this.m_upperAngle = def.upperAngle;
    this.m_maxMotorTorque = def.maxMotorTorque;
    this.m_motorSpeed = def.motorSpeed;
    this.m_enableLimit = def.enableLimit;
    this.m_enableMotor = def.enableMotor;
    this.m_limitState = b2Joint.e_inactiveLimit;
    this.m_pivotMass = new b2Mat22();
    this.K1 = new b2Mat22();
    this.K2 = new b2Mat22();
    this.K3 = new b2Mat22();
    this.K = new b2Mat22();
    this.m_motorMass = 0;
  }

  GetAnchorA() {
    return this.m_bodyA.GetWorldPoint(this.m_localAnchor1);
  }

  GetAnchorB() {
    return this.m_bodyB.GetWorldPoint(this.m_localAnchor2);
  }

  GetReactionForce() {
    return this.m_pivotForce;
  }

  GetReactionTorque() {
    return this.m_limitForce;
  }

  GetJointAngle() {
    return this.m_bodyB.m_sweep.a - this.m_bodyA.m_sweep.a - this.m_referenceAngle;
  }

  GetJointSpeed() {
    return this.m_bodyB.m_angularVelocity - this.m_bodyA.m_angularVelocity;
  }

  GetLowerLimit() {
    return this.m_lowerAngle;
  }

  GetUpperLimit() {
    return this.m_upperAngle;
  }

  SetLimits(lower, upper) {
    this.m_lowerAngle = lower;
    this.m_upperAngle = upper;
  }

  IsLimitEnabled() {
    return this.m_enableLimit;
  }

  EnableLimit(flag) {
    this.m_enableLimit = flag;
  }

  IsMotorEnabled() {
    return this.m_enableMotor;
  }

  EnableMotor(flag) {
    this.m_enableMotor = flag;
  }

  SetMotorSpeed(speed) {
    this.m_motorSpeed = speed;
  }

  GetMotorSpeed() {
    return this.m_motorSpeed;
  }

  SetMaxMotorTorque(torque) {
    this.m_maxMotorTorque = torque;
  }

  GetMotorTorque() {
    return this.m_motorForce;
  }

  InitVelocityConstraints(step) {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    const invMassA = bodyA.m_invMass;
    const invMassB = bodyB.m_invMass;
    const invIA = bodyA.m_invI;
    const invIB = bodyB.m_invI;
    this.K1.col1.x = invMassA + invMassB;
    this.K1.col2.x = 0;
    this.K1.col1.y = 0;
    this.K1.col2.y = invMassA + invMassB;
    this.K2.col1.x = invIA * localY * localY;
    this.K2.col2.x = -invIA * localX * localY;
    this.K2.col1.y = -invIA * localX * localY;
    this.K2.col2.y = invIA * localX * localX;
    this.K3.col1.x = invIB * otherLocalY * otherLocalY;
    this.K3.col2.x = -invIB * otherLocalX * otherLocalY;
    this.K3.col1.y = -invIB * otherLocalX * otherLocalY;
    this.K3.col2.y = invIB * otherLocalX * otherLocalX;
    this.K.SetM(this.K1);
    this.K.AddM(this.K2);
    this.K.AddM(this.K3);
    this.K.Invert(this.m_pivotMass);
    this.m_motorMass = 1 / (invIA + invIB);
    if (this.m_enableMotor === false) {
      this.m_motorForce = 0;
    }
    if (this.m_enableLimit) {
      const jointAngle = bodyB.m_sweep.a - bodyA.m_sweep.a - this.m_referenceAngle;
      if (b2Math.Abs(this.m_upperAngle - this.m_lowerAngle) < 2 * b2Settings.b2_angularSlop) {
        this.m_limitState = b2Joint.e_equalLimits;
      } else if (jointAngle <= this.m_lowerAngle) {
        if (this.m_limitState !== b2Joint.e_atLowerLimit) {
          this.m_limitForce = 0;
        }
        this.m_limitState = b2Joint.e_atLowerLimit;
      } else if (jointAngle >= this.m_upperAngle) {
        if (this.m_limitState !== b2Joint.e_atUpperLimit) {
          this.m_limitForce = 0;
        }
        this.m_limitState = b2Joint.e_atUpperLimit;
      } else {
        this.m_limitState = b2Joint.e_inactiveLimit;
        this.m_limitForce = 0;
      }
    } else {
      this.m_limitForce = 0;
    }
    if (step.warmStarting) {
      bodyA.m_linearVelocity.x -= step.dt * invMassA * this.m_pivotForce.x;
      bodyA.m_linearVelocity.y -= step.dt * invMassA * this.m_pivotForce.y;
      bodyA.m_angularVelocity -= step.dt * invIA * (localX * this.m_pivotForce.y - localY * this.m_pivotForce.x + this.m_motorForce + this.m_limitForce);
      bodyB.m_linearVelocity.x += step.dt * invMassB * this.m_pivotForce.x;
      bodyB.m_linearVelocity.y += step.dt * invMassB * this.m_pivotForce.y;
      bodyB.m_angularVelocity += step.dt * invIB * (otherLocalX * this.m_pivotForce.y - otherLocalY * this.m_pivotForce.x + this.m_motorForce + this.m_limitForce);
    } else {
      this.m_pivotForce.SetZero();
      this.m_motorForce = 0;
      this.m_limitForce = 0;
    }
    this.m_limitPositionImpulse = 0;
  }

  SolveVelocityConstraints(step) {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    const dvX = bodyB.m_linearVelocity.x - bodyB.m_angularVelocity * otherLocalY - bodyA.m_linearVelocity.x + bodyA.m_angularVelocity * localY;
    const dvY = bodyB.m_linearVelocity.y + bodyB.m_angularVelocity * otherLocalX - bodyA.m_linearVelocity.y - bodyA.m_angularVelocity * localX;
    const forceX = -step.inv_dt * (this.m_pivotMass.col1.x * dvX + this.m_pivotMass.col2.x * dvY);
    const forceY = -step.inv_dt * (this.m_pivotMass.col1.y * dvX + this.m_pivotMass.col2.y * dvY);
    this.m_pivotForce.x += forceX;
    this.m_pivotForce.y += forceY;
    const impulseX = step.dt * forceX;
    const impulseY = step.dt * forceY;
    bodyA.m_linearVelocity.x -= bodyA.m_invMass * impulseX;
    bodyA.m_linearVelocity.y -= bodyA.m_invMass * impulseY;
    bodyA.m_angularVelocity -= bodyA.m_invI * (localX * impulseY - localY * impulseX);
    bodyB.m_linearVelocity.x += bodyB.m_invMass * impulseX;
    bodyB.m_linearVelocity.y += bodyB.m_invMass * impulseY;
    bodyB.m_angularVelocity += bodyB.m_invI * (otherLocalX * impulseY - otherLocalY * impulseX);
    if (this.m_enableMotor && this.m_limitState !== b2Joint.e_equalLimits) {
      const motorVelocity = bodyB.m_angularVelocity - bodyA.m_angularVelocity - this.m_motorSpeed;
      let force = -step.inv_dt * this.m_motorMass * motorVelocity;
      const previous = this.m_motorForce;
      this.m_motorForce = b2Math.Clamp(this.m_motorForce + force, -this.m_maxMotorTorque, this.m_maxMotorTorque);
      force = this.m_motorForce - previous;
      bodyA.m_angularVelocity -= bodyA.m_invI * step.dt * force;
      bodyB.m_angularVelocity += bodyB.m_invI * step.dt * force;
    }
    if (this.m_enableLimit && this.m_limitState !== b2Joint.e_inactiveLimit) {
      const limitVelocity = bodyB.m_angularVelocity - bodyA.m_angularVelocity;
      let force = -step.inv_dt * this.m_motorMass * limitVelocity;
      if (this.m_limitState === b2Joint.e_equalLimits) {
        this.m_limitForce += force;
      } else if (this.m_limitState === b2Joint.e_atLowerLimit) {
        const previous = this.m_limitForce;
        this.m_limitForce = b2Math.Max(this.m_limitForce + force, 0);
        force = this.m_limitForce - previous;
      } else if (this.m_limitState === b2Joint.e_atUpperLimit) {
        const previous = this.m_limitForce;
        this.m_limitForce = b2Math.Min(this.m_limitForce + force, 0);
        force = this.m_limitForce - previous;
      }
      bodyA.m_angularVelocity -= bodyA.m_invI * step.dt * force;
      bodyB.m_angularVelocity += bodyB.m_invI * step.dt * force;
    }
  }

  SolvePositionConstraints() {
    const bodyA = this.m_bodyA;
    const bodyB = this.m_bodyB;
    let rotation = bodyA.m_xf.R;
    let localX = this.m_localAnchor1.x - bodyA.m_sweep.localCenter.x;
    let localY = this.m_localAnchor1.y - bodyA.m_sweep.localCenter.y;
    let rotated = rotation.col1.x * localX + rotation.col2.x * localY;
    localY = rotation.col1.y * localX + rotation.col2.y * localY;
    localX = rotated;
    rotation = bodyB.m_xf.R;
    let otherLocalX = this.m_localAnchor2.x - bodyB.m_sweep.localCenter.x;
    let otherLocalY = this.m_localAnchor2.y - bodyB.m_sweep.localCenter.y;
    rotated = rotation.col1.x * otherLocalX + rotation.col2.x * otherLocalY;
    otherLocalY = rotation.col1.y * otherLocalX + rotation.col2.y * otherLocalY;
    otherLocalX = rotated;
    const pointAX = bodyA.m_sweep.c.x + localX;
    const pointAY = bodyA.m_sweep.c.y + localY;
    const pointBX = bodyB.m_sweep.c.x + otherLocalX;
    const pointBY = bodyB.m_sweep.c.y + otherLocalY;
    const dx = pointBX - pointAX;
    const dy = pointBY - pointAY;
    const positionError = Math.sqrt(dx * dx + dy * dy);
    const invMassA = bodyA.m_invMass;
    const invMassB = bodyB.m_invMass;
    const invIA = bodyA.m_invI;
    const invIB = bodyB.m_invI;
    this.K1.col1.x = invMassA + invMassB;
    this.K1.col2.x = 0;
    this.K1.col1.y = 0;
    this.K1.col2.y = invMassA + invMassB;
    this.K2.col1.x = invIA * localY * localY;
    this.K2.col2.x = -invIA * localX * localY;
    this.K2.col1.y = -invIA * localX * localY;
    this.K2.col2.y = invIA * localX * localX;
    this.K3.col1.x = invIB * otherLocalY * otherLocalY;
    this.K3.col2.x = -invIB * otherLocalX * otherLocalY;
    this.K3.col1.y = -invIB * otherLocalX * otherLocalY;
    this.K3.col2.y = invIB * otherLocalX * otherLocalX;
    this.K.SetM(this.K1);
    this.K.AddM(this.K2);
    this.K.AddM(this.K3);
    this.K.Solve(b2RevoluteJoint.tImpulse, -dx, -dy);
    const impulseX = b2RevoluteJoint.tImpulse.x;
    const impulseY = b2RevoluteJoint.tImpulse.y;
    bodyA.m_sweep.c.x -= bodyA.m_invMass * impulseX;
    bodyA.m_sweep.c.y -= bodyA.m_invMass * impulseY;
    bodyA.m_sweep.a -= bodyA.m_invI * (localX * impulseY - localY * impulseX);
    bodyB.m_sweep.c.x += bodyB.m_invMass * impulseX;
    bodyB.m_sweep.c.y += bodyB.m_invMass * impulseY;
    bodyB.m_sweep.a += bodyB.m_invI * (otherLocalX * impulseY - otherLocalY * impulseX);
    bodyA.SynchronizeTransform();
    bodyB.SynchronizeTransform();
    let angularError = 0;
    if (this.m_enableLimit && this.m_limitState !== b2Joint.e_inactiveLimit) {
      const jointAngle = bodyB.m_sweep.a - bodyA.m_sweep.a - this.m_referenceAngle;
      let impulse = 0;
      if (this.m_limitState === b2Joint.e_equalLimits) {
        let constraint = b2Math.Clamp(jointAngle, -b2Settings.b2_maxAngularCorrection, b2Settings.b2_maxAngularCorrection);
        impulse = -this.m_motorMass * constraint;
        angularError = b2Math.Abs(constraint);
      } else if (this.m_limitState === b2Joint.e_atLowerLimit) {
        let constraint = jointAngle - this.m_lowerAngle;
        angularError = b2Math.Max(0, -constraint);
        constraint = b2Math.Clamp(constraint + b2Settings.b2_angularSlop, -b2Settings.b2_maxAngularCorrection, 0);
        impulse = -this.m_motorMass * constraint;
        const previous = this.m_limitPositionImpulse;
        this.m_limitPositionImpulse = b2Math.Max(this.m_limitPositionImpulse + impulse, 0);
        impulse = this.m_limitPositionImpulse - previous;
      } else if (this.m_limitState === b2Joint.e_atUpperLimit) {
        let constraint = jointAngle - this.m_upperAngle;
        angularError = b2Math.Max(0, constraint);
        constraint = b2Math.Clamp(constraint - b2Settings.b2_angularSlop, 0, b2Settings.b2_maxAngularCorrection);
        impulse = -this.m_motorMass * constraint;
        const previous = this.m_limitPositionImpulse;
        this.m_limitPositionImpulse = b2Math.Min(this.m_limitPositionImpulse + impulse, 0);
        impulse = this.m_limitPositionImpulse - previous;
      }
      bodyA.m_sweep.a -= bodyA.m_invI * impulse;
      bodyB.m_sweep.a += bodyB.m_invI * impulse;
      bodyA.SynchronizeTransform();
      bodyB.SynchronizeTransform();
    }
    return positionError <= b2Settings.b2_linearSlop && angularError <= b2Settings.b2_angularSlop;
  }
}
